<template>
  <div class="company-programmes-mobile-list">
    <article
      v-for="row in rows"
      :key="get_card_key(row)"
      class="company-programme-card"
      :class="{ 'company-programme-card--sub': is_sub_program(row) }"
    >
      <div class="company-programme-card-main">
        <div class="company-programme-card-badge" aria-hidden="true">
          <span>{{ get_programme_initials(row) }}</span>
        </div>

        <div class="company-programme-card-copy">
          <div class="company-programme-card-topline">
            <div class="company-programme-card-heading">
              <h3 class="company-programme-card-title">{{ get_row_value(row, "Programme", "Untitled Programme") }}</h3>
            </div>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-card-button company-programme-card-button--view"
              type="button"
              @click="$emit('view-programme', row)"
            >
              View
            </button>
          </div>
          <p v-if="has_meaningful_description(row)" class="company-programme-card-description">{{ get_row_value(row, "Description", "-") }}</p>
          <dl class="company-programme-card-meta">
            <div class="company-programme-card-meta-row">
              <dt>Type</dt>
              <dd>{{ get_row_value(row, "Type", "-") }}</dd>
            </div>
            <div class="company-programme-card-meta-row">
              <dt>Date Created</dt>
              <dd>{{ get_display_date(row) }}</dd>
            </div>
            <div class="company-programme-card-meta-row">
              <dt>Assigned Administrator</dt>
              <dd>{{ get_row_value(row, "Assigned Administrator", "-") }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div class="company-programme-card-actions">
        <button
          v-if="!is_sub_program(row)"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-card-button company-programme-card-icon-button"
          type="button"
          aria-label="Create sub programme"
          title="Create sub programme"
          @click="$emit('create-sub-programme', row.id)"
        >
          <i class="fa-solid fa-diagram-project" aria-hidden="true"></i>
        </button>
        <button
          v-if="!is_sub_program(row)"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white company-programme-card-button company-programme-card-icon-button"
          type="button"
          :aria-label="is_expanded(row.id) ? 'Hide sub programmes' : 'Show sub programmes'"
          :title="is_expanded(row.id) ? 'Hide sub programmes' : 'Show sub programmes'"
          @click="$emit('toggle-programme', row.id)"
        >
          <i :class="is_expanded(row.id) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" aria-hidden="true"></i>
        </button>
        <button
          v-if="!is_sub_program(row)"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-card-button company-programme-card-icon-button"
          type="button"
          aria-label="Edit programme"
          title="Edit programme"
          @click="$emit('edit-programme', row.id)"
        >
          <i class="fa-solid fa-pen" aria-hidden="true"></i>
        </button>
        <button
          v-if="!is_sub_program(row)"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-card-button company-programme-card-icon-button"
          type="button"
          aria-label="Deactivate programme"
          title="Deactivate programme"
          @click="$emit('deactivate-programme', row.id)"
        >
          <i class="fa-solid fa-ban" aria-hidden="true"></i>
        </button>
      </div>

      <section
        v-if="!is_sub_program(row) && is_expanded(row.id)"
        class="company-programme-card-subprogrammes"
      >
        <p class="company-programme-card-subtitle">Sub Programmes</p>

        <div v-if="has_sub_programmes(row.id)" class="company-programme-sub-list">
          <article
            v-for="sub_programme in get_sub_programmes(row.id)"
            :key="sub_programme.id"
            class="company-programme-sub-card"
          >
            <div class="company-programme-sub-copy">
              <p class="company-programme-sub-title">{{ sub_programme.name }}</p>
              <p v-if="sub_programme.description" class="company-programme-sub-description">{{ sub_programme.description }}</p>
              <p class="company-programme-sub-date">Created {{ format_date_short(sub_programme.date_created) }}</p>
            </div>
            <div class="company-programme-sub-actions">
              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-sub-button"
                type="button"
                @click="$emit('view-sub-programme-jobs', sub_programme, row)"
              >
                View Jobs
              </button>
              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-sub-button"
                type="button"
                @click="$emit('edit-sub-programme', sub_programme, row.id)"
              >
                Edit
              </button>
              <button
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark company-programme-sub-button"
                type="button"
                @click="$emit('deactivate-sub-programme', sub_programme.id, row.id)"
              >
                Deactivate
              </button>
            </div>
          </article>
        </div>

        <p v-else-if="has_loaded_sub_programmes(row.id)" class="company-programme-card-empty">
          No sub-programmes yet.
        </p>
        <p v-else class="company-programme-card-loading">Loading sub-programmes...</p>
      </section>
    </article>

    <div v-if="!rows.length" class="company-programme-card-empty-state">
      No programmes found.
    </div>
  </div>
</template>

<script>
export default {
  name: "CompanyProgrammesResponsiveList",
  props: {
    rows: { type: Array, required: true },
    expanded_programs: { type: Object, required: true },
    sub_programs: { type: Object, required: true },
    format_date_short: { type: Function, required: true },
  },
  emits: [
    "view-programme",
    "create-sub-programme",
    "toggle-programme",
    "edit-programme",
    "deactivate-programme",
    "view-sub-programme-jobs",
    "edit-sub-programme",
    "deactivate-sub-programme",
  ],
  methods: {
    /** Return a stable key for each programme card. */
    get_card_key(row) {
      const suffix = this.is_sub_program(row) ? "sub" : "main";
      return `${Reflect.get(row || {}, "id") || Reflect.get(row || {}, "Programme") || "programme"}-${suffix}`;
    },
    /** Read a display-safe row value without dynamic template access. */
    get_row_value(row, key, fallback = "") {
      const row_value = Reflect.get(row || {}, key);
      return row_value || fallback;
    },
    /** Determine whether the row is a sub-programme row. */
    is_sub_program(row) {
      const is_sub = Reflect.get(row || {}, "_is_sub_program");
      return Boolean(is_sub);
    },
    /** Build the compact badge initials for the programme card. */
    get_programme_initials(row) {
      const raw_title = this.get_row_value(row, "Programme", "Programme");
      const parts = String(raw_title).split(" ").filter(Boolean).slice(0, 2);
      const initials = parts.map((part) => part.charAt(0).toUpperCase()).join("") || "PR";
      return initials;
    },
    /** Avoid repeating the title as the description when both fields match. */
    has_meaningful_description(row) {
      const title = String(this.get_row_value(row, "Programme", "")).trim().toLowerCase();
      const description = String(this.get_row_value(row, "Description", "")).trim();
      if (!description || description === "-") return false;
      return description.toLowerCase() !== title;
    },
    /** Keep only the date portion for the compact mobile card. */
    get_display_date(row) {
      const raw_date = String(this.get_row_value(row, "Date Created", "-")).trim();
      if (!raw_date || raw_date === "-") return "-";
      const [date_only] = raw_date.split(" ");
      return date_only || raw_date;
    },
    /** Return the current accordion state for the main programme card. */
    is_expanded(project_id) {
      const is_open = Reflect.get(this.expanded_programs || {}, project_id);
      return Boolean(is_open);
    },
    /** Return the loaded sub-programmes for the given parent project. */
    get_sub_programmes(project_id) {
      const sub_programmes = Reflect.get(this.sub_programs || {}, project_id);
      return Array.isArray(sub_programmes) ? sub_programmes : [];
    },
    /** Check whether the project has loaded sub-programmes. */
    has_loaded_sub_programmes(project_id) {
      const sub_programmes = Reflect.get(this.sub_programs || {}, project_id);
      return Array.isArray(sub_programmes);
    },
    /** Check whether the project has one or more sub-programmes to render. */
    has_sub_programmes(project_id) {
      const sub_programmes = this.get_sub_programmes(project_id);
      return sub_programmes.length > 0;
    },
  },
};
</script>

<style scoped>
.company-programmes-mobile-list {
  display: none;
}

@media (width <= 1199.98px) {
  .company-programmes-mobile-list {
    display: grid;
    gap: 14px;
  }

  .company-programme-card {
    padding: 14px;
    border: 1px solid #e6ebf1;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 4px 12px rgb(15 23 42 / 5%);
  }

  .company-programme-card--sub {
    background: #fbfcfd;
  }

  .company-programme-card-main {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .company-programme-card-badge {
    width: 56px;
    height: 56px;
    border: 1px solid #ece5bf;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, #fff6cf 0%, #f9ecc0 100%);
    color: #111827;
    font-size: 0.9rem;
    font-weight: 800;
  }

  .company-programme-card-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .company-programme-card-heading,
  .company-programme-card-meta-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .company-programme-card-title,
  .company-programme-sub-title {
    margin: 0;
    color: #111827;
    font-weight: 800;
  }

  .company-programme-card-title {
    font-size: 1rem;
    line-height: 1.3;
  }

  .company-programme-card-meta dt {
    color: #7b8594;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .company-programme-card-description,
  .company-programme-card-meta dd,
  .company-programme-sub-description,
  .company-programme-sub-date,
  .company-programme-card-empty,
  .company-programme-card-loading,
  .company-programme-card-empty-state {
    margin: 0;
    color: #4b5563;
    font-size: 0.78rem;
  }

  .company-programme-card-description {
    margin-top: 6px;
  }

  .company-programme-card-meta {
    margin: 10px 0 0;
    display: grid;
    gap: 6px;
  }

  .company-programme-card-meta-row dd {
    text-align: right;
    font-weight: 600;
  }

  .company-programme-card-actions,
  .company-programme-sub-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .company-programme-card-button,
  .company-programme-sub-button {
    width: auto;
    min-height: 30px;
    padding: 0.35rem 0.8rem;
    border: 1px solid #d9e0e7 !important;
    border-radius: 999px !important;
    background: #fff !important;
    color: #111827 !important;
    box-shadow: none !important;
    font-weight: 700;
  }

  .company-programme-card-button--view {
    min-width: 82px;
    border-color: #050505 !important;
    background: #050505 !important;
    color: #fff !important;
    flex: 0 0 auto;
  }

  .company-programme-card-icon-button {
    width: 38px;
    min-width: 38px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .company-programme-card-icon-button i {
    font-size: 0.82rem;
  }

  .company-programme-card-subprogrammes {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e5eaf0;
  }

  .company-programme-card-subtitle {
    margin: 0 0 10px;
    color: #111827;
    font-size: 0.84rem;
    font-weight: 700;
  }

  .company-programme-sub-list {
    display: grid;
    gap: 10px;
  }

  .company-programme-sub-card {
    padding: 12px;
    border: 1px solid #e5eaf0;
    border-radius: 14px;
    background: #f9fbfc;
  }

  .company-programme-sub-description,
  .company-programme-sub-date {
    margin-top: 4px;
  }

  .company-programme-card-title,
  .company-programme-card-description,
  .company-programme-card-meta dd,
  .company-programme-sub-title,
  .company-programme-sub-description,
  .company-programme-sub-date {
    overflow-wrap: anywhere;
  }
}

@media (width <= 575.98px) {
  .company-programme-card-main {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 10px;
  }

  .company-programme-card-badge {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    font-size: 0.82rem;
  }

  .company-programme-card-topline {
    align-items: stretch;
    flex-direction: column;
  }

  .company-programme-card-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .company-programme-card-button--view {
    width: 100%;
  }

  .company-programme-card-meta-row {
    flex-direction: column;
    gap: 2px;
  }

  .company-programme-card-meta-row dd {
    text-align: left;
  }
}
</style>
